import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, KeyRound, Nfc, Search, Settings2, ShieldAlert, UserCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { AvatarPreview } from '@/components/digitalOffice/AvatarPreview';
import { WorkPinPad } from './WorkPinPad';
import { useBadgeScanner } from '@/features/workMode/kiosk';
import { describeWorkError } from '@/features/workMode/api';
import type { AvatarConfig } from '@/features/digitalOffice/avatars';
import type { FloorWorkstation, WorkSessionStartResult } from '@/features/workMode/types';

export interface LockScreenPerson {
  id: string;
  name: string;
  avatarUrl: string | null;
  config: AvatarConfig;
  hasPin: boolean;
  hasBadge: boolean;
  lockedUntil: string | null;
  /** Name of the station they are currently signed in at, if any. */
  workingAt: string | null;
}

interface WorkLockScreenProps {
  station: FloorWorkstation;
  itemName: string | null;
  people: LockScreenPerson[];
  me: { id: string; name: string } | null;
  busy: boolean;
  onStart: (input: { userId?: string | null; pin?: string | null; badge?: string | null }) => Promise<WorkSessionStartResult>;
  onChangeStation: () => void;
}

/**
 * Who's sitting down? Tap your face → PIN, or just scan your fob. The
 * signed-in Atlas account can also "continue as me" without a PIN.
 *
 * "Change station" is open to whoever is at the tablet: a worker who
 * moves benches (or signs in on their own device) re-picks the station
 * here. The binding is only a device-local preference — every unit is
 * still attributed to the station the shift was started on.
 */
export function WorkLockScreen({ station, itemName, people, me, busy, onStart, onChangeStation }: WorkLockScreenProps) {
  const [selected, setSelected] = useState<LockScreenPerson | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resetKey, setResetKey] = useState(0);
  const [q, setQ] = useState('');
  const [scanFlash, setScanFlash] = useState<'ok' | 'bad' | null>(null);

  useEffect(() => {
    setError(null);
  }, [selected?.id]);

  const handleResult = (res: WorkSessionStartResult) => {
    if (res.ok) return;
    setError(describeWorkError(res.error));
    setResetKey((k) => k + 1);
  };

  useBadgeScanner(!busy, async (code) => {
    setError(null);
    try {
      const res = await onStart({ badge: code });
      if (!res.ok) {
        setScanFlash('bad');
        setError(describeWorkError(res.error));
      } else {
        setScanFlash('ok');
      }
    } catch (e) {
      setScanFlash('bad');
      setError(describeWorkError(e));
    } finally {
      window.setTimeout(() => setScanFlash(null), 1500);
    }
  });

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return people.filter((p) => !needle || p.name.toLowerCase().includes(needle));
  }, [people, q]);

  const submitPin = async (pin: string) => {
    if (!selected) return;
    setError(null);
    try {
      handleResult(await onStart({ userId: selected.id, pin }));
    } catch (e) {
      setError(describeWorkError(e));
      setResetKey((k) => k + 1);
    }
  };

  const continueAsMe = async () => {
    if (!me) return;
    setError(null);
    try {
      handleResult(await onStart({ userId: me.id }));
    } catch (e) {
      setError(describeWorkError(e));
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex items-center justify-between gap-4 border-b border-border px-6 py-4">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Station</p>
          <h1 className="truncate text-2xl font-medium tracking-headline text-foreground">{station.name}</h1>
          {itemName && <p className="truncate text-sm text-muted-foreground">Building: {itemName}</p>}
          {station.ppe.length > 0 && (
            <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
              <ShieldAlert className="h-3.5 w-3.5 text-fresh-salmon-dark" aria-hidden />
              {station.ppe.map((p) => (
                <span key={p} className="rounded-full border border-fresh-salmon/60 bg-fresh-salmon/15 px-2 py-0.5 font-medium text-fresh-salmon-dark">
                  {p}
                </span>
              ))}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div
            className={cn(
              'hidden items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors sm:flex',
              scanFlash === 'ok'
                ? 'border-bright-moss bg-bright-moss/30 text-bright-moss-dark'
                : scanFlash === 'bad'
                  ? 'border-fresh-salmon bg-fresh-salmon/25 text-fresh-salmon-dark'
                  : 'border-border bg-card text-muted-foreground',
            )}
          >
            <Nfc className={cn('h-4 w-4', scanFlash === null && 'animate-pulse')} />
            Scan your badge any time
          </div>
          <Button variant="ghost" onClick={onChangeStation} aria-label="Change station" className="gap-2">
            <Settings2 className="h-5 w-5" />
            <span className="hidden sm:inline">Change station</span>
          </Button>
        </div>
      </header>

      <div className="grid min-h-0 flex-1 gap-0 lg:grid-cols-[1.4fr_1fr]">
        <section className="flex min-h-0 flex-col border-b border-border lg:border-b-0 lg:border-r">
          <div className="flex items-center gap-3 px-6 py-3">
            <p className="text-sm font-medium text-foreground">Who's working here?</p>
            <div className="relative ml-auto w-full max-w-xs">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a name" className="h-10 pl-9" />
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
            {filtered.length === 0 ? (
              <p className="py-10 text-center text-sm text-muted-foreground">Nobody matches that name.</p>
            ) : (
              <ul className="grid grid-cols-3 gap-3 sm:grid-cols-4 xl:grid-cols-5">
                {filtered.map((p) => {
                  const active = selected?.id === p.id;
                  const locked = p.lockedUntil ? Date.parse(p.lockedUntil) > Date.now() : false;
                  return (
                    <li key={p.id}>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => setSelected(p)}
                        className={cn(
                          'flex w-full flex-col items-center gap-2 rounded-2xl border p-3 text-center transition-colors',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                          active ? 'border-info bg-info-soft/30' : 'border-border bg-card hover:bg-secondary',
                        )}
                      >
                        <PersonFace person={p} size={64} />
                        <span className="line-clamp-2 text-sm font-medium leading-tight text-foreground">{p.name}</span>
                        {p.workingAt ? (
                          <span className="rounded-full bg-bright-moss/40 px-2 py-0.5 text-[10px] font-medium text-bright-moss-dark">At {p.workingAt}</span>
                        ) : locked ? (
                          <span className="rounded-full bg-fresh-salmon/25 px-2 py-0.5 text-[10px] font-medium text-fresh-salmon-dark">Locked</span>
                        ) : !p.hasPin && !p.hasBadge ? (
                          <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-medium text-muted-foreground">No PIN yet</span>
                        ) : (
                          <span className="h-[18px]" />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>

        <section className="flex min-h-0 flex-col items-center justify-center px-6 py-6">
          {selected ? (
            <div className="flex w-full max-w-sm flex-col items-center gap-5">
              <button type="button" onClick={() => setSelected(null)} className="self-start text-sm text-muted-foreground hover:text-foreground">
                <ArrowLeft className="mr-1 inline h-4 w-4" />
                Not {selected.name.split(' ')[0]}?
              </button>
              <PersonFace person={selected} size={88} />
              <div className="text-center">
                <p className="text-xl font-medium text-foreground">Hi, {selected.name.split(' ')[0]}</p>
                <p className="text-sm text-muted-foreground">{selected.hasPin ? 'Enter your PIN to start.' : 'No PIN set yet — scan your badge or ask a manager.'}</p>
              </div>
              <WorkPinPad onSubmit={submitPin} busy={busy} error={error} resetKey={resetKey} />
            </div>
          ) : (
            <div className="flex w-full max-w-sm flex-col items-center gap-6 text-center">
              <span className="flex h-20 w-20 items-center justify-center rounded-full bg-secondary text-muted-foreground">
                <KeyRound className="h-9 w-9" />
              </span>
              <div>
                <p className="text-xl font-medium text-foreground">Tap your name to sign in</p>
                <p className="text-sm text-muted-foreground">Or hold your fob against the reader.</p>
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              {me && (
                <Button variant="outline" size="lg" onClick={continueAsMe} disabled={busy} className="w-full">
                  <UserCheck className="h-5 w-5" />
                  Continue as {me.name}
                </Button>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export function PersonFace({ person, size = 64, className }: { person: { name: string; avatarUrl: string | null; config: AvatarConfig }; size?: number; className?: string }) {
  if (person.avatarUrl) {
    return <img src={person.avatarUrl} alt="" style={{ width: size, height: size }} className={cn('rounded-full object-cover', className)} draggable={false} />;
  }
  return (
    <span style={{ width: size, height: size }} className={cn('flex items-center justify-center overflow-hidden rounded-full bg-linen', className)}>
      <AvatarPreview config={person.config} size={Math.round(size * 0.9)} />
    </span>
  );
}
